import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, ApolloError } from "@apollo/client";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import useModal from "@/hooks/useModal";
import { useNavigate } from "@/router";
import { isDev } from "@/lib/urlUtils";
import { ProgramStatus, ProgramTypeName } from "@/types";
import {
  CREATE_TEST_PERSON_MUTATION,
  CREATE_TEST_PERSON_OPTIONS_QUERY,
  type CreateTestPersonInput
} from "../mutations";
import {
  defaultCompletedPgmLevels,
  graduatedStudentCompletedPgmLevels,
  pgmLevelOptionsForProgram,
  toggleCompletedPgmLevel
} from "./createTestPersonPgmLevels";
import ProgramSection from "./createTestPerson/ProgramSection";
import MembershipSection from "./createTestPerson/MembershipSection";
import PgmPathSection from "./createTestPerson/PgmPathSection";
import EligibilitySection from "./createTestPerson/EligibilitySection";
import PatSection from "./createTestPerson/PatSection";
import type { CreateTestPersonFormValues, CreateTestPersonPatStatus } from "./createTestPerson/types";
import { showSnackbar } from "@/lib/snackbarUtils";

const NONE_OPTION = { value: "", label: "None" };

const PROGRAM_TYPE_OPTIONS = [
  NONE_OPTION,
  ...Object.keys(ProgramTypeName).map((key) => ({
    value: key.toUpperCase(),
    label: ProgramTypeName[key as keyof typeof ProgramTypeName]
  }))
];

const STATUS_OPTIONS = [
  NONE_OPTION,
  ...Object.keys(ProgramStatus).map((key) => ({
    value: key,
    label: key.charAt(0) + key.slice(1).toLowerCase()
  }))
];

const PROGRAM_TYPE_NAME_TO_ENUM: Record<ProgramTypeName, string> = {
  [ProgramTypeName.Member]: "MEMBER",
  [ProgramTypeName.Associate]: "ASSOCIATE",
  [ProgramTypeName.Affiliate]: "AFFILIATE",
  [ProgramTypeName.Student]: "STUDENT",
  [ProgramTypeName.Prospect]: "PROSPECT"
};

const DEFAULT_PROGRAM_TYPE = "PROSPECT";
const DEFAULT_CLASSIFICATION_NAME = "B-8";
const DEFAULT_SECTION_NAME = "Carolinas";
const DEFAULT_STATUS = ProgramStatus.ACTIVE;

export function defaultPatStatusForProgram(programType: string, status: string): CreateTestPersonPatStatus {
  if (!programType) {
    return "NONE";
  }

  if (programType === "PROSPECT" || programType === "STUDENT" || programType === "AFFILIATE") {
    return "QUALIFYING";
  }

  if (programType === "ASSOCIATE" || programType === "MEMBER") {
    // Blank status is omitted from the mutation and backend defaults to active.
    if (status === "SUSPENDED") {
      return "NONE";
    }
    // Terminated associates default to passed PAT for re-register QA; terminated members stay none.
    if (status === "TERMINATED") {
      return programType === "ASSOCIATE" ? "PASSED" : "NONE";
    }
    return "PASSED";
  }

  return "NONE";
}

export function prospectQualifierEligibleFromPgmLevels(completedPgmLevelCodes: string[]): boolean {
  return completedPgmLevelCodes.includes("F0");
}

const defaultValues: CreateTestPersonFormValues = {
  programType: DEFAULT_PROGRAM_TYPE,
  classificationId: "",
  sectionId: "",
  status: DEFAULT_STATUS,
  completedPgmLevelCodes: defaultCompletedPgmLevels(DEFAULT_PROGRAM_TYPE),
  includeApprovedBackgroundCheck: true,
  droppedPgmStudent: false,
  graduatedPgmStudent: false,
  includePreQualifierBand: true,
  patStatus: defaultPatStatusForProgram(DEFAULT_PROGRAM_TYPE, DEFAULT_STATUS),
  includeAssociateL1Progress: false,
  includeOpenDues: false,
  ineligiblyEmployed: false,
  pendingAffiliateRegistration: false,
  expiredPat: false
};

type OptionsData = {
  classifications: {
    nodes: { id: string; name: string; programType: { id: string; name: string } }[];
  };
  crmSections: { nodes: { id: string; name: string }[] };
};

export function membershipDefaultsForProgram(
  programType: string,
  optionsData?: OptionsData
): Pick<CreateTestPersonFormValues, "classificationId" | "status" | "sectionId" | "completedPgmLevelCodes"> {
  const classifications = optionsData?.classifications.nodes ?? [];
  const sections = optionsData?.crmSections.nodes ?? [];

  const matchingClassifications = classifications.filter(
    (node) => PROGRAM_TYPE_NAME_TO_ENUM[node.programType.name as ProgramTypeName] === programType
  );

  const classification =
    matchingClassifications.find((node) => node.name === DEFAULT_CLASSIFICATION_NAME) ?? matchingClassifications[0];

  const section = sections.find((node) => node.name === DEFAULT_SECTION_NAME) ?? sections[0];

  return {
    classificationId: classification?.id ?? "",
    status: DEFAULT_STATUS,
    sectionId: section?.id ?? "",
    completedPgmLevelCodes: defaultCompletedPgmLevels(programType)
  };
}

function resetQaOverlayFlags(setValue: (name: keyof CreateTestPersonFormValues, value: boolean) => void) {
  setValue("includeAssociateL1Progress", false);
  setValue("includeOpenDues", false);
  setValue("ineligiblyEmployed", false);
  setValue("pendingAffiliateRegistration", false);
  setValue("expiredPat", false);
}

export function toMutationInput(values: CreateTestPersonFormValues): CreateTestPersonInput {
  const input: CreateTestPersonInput = {};
  const programType = values.programType;

  if (programType) {
    input.programType = programType;
    input.includeApprovedBackgroundCheck = values.includeApprovedBackgroundCheck;
  }
  if (values.classificationId) {
    input.classificationId = values.classificationId;
  }
  if (values.status) {
    input.status = values.status as ProgramStatus;
  }
  if (values.sectionId) {
    input.sectionId = values.sectionId;
  }
  if (programType) {
    input.completedPgmLevelCodes = values.completedPgmLevelCodes;
  }
  if (programType === "PROSPECT") {
    input.includePreQualifierBand = values.includePreQualifierBand;
    input.includeQualifierEligibleBand = prospectQualifierEligibleFromPgmLevels(values.completedPgmLevelCodes);
  }
  if (programType === "STUDENT") {
    input.droppedPgmStudent = values.droppedPgmStudent;
    input.graduatedPgmStudent = values.graduatedPgmStudent;
  }
  if (programType) {
    input.patStatus = values.patStatus;
  }
  if (programType === "AFFILIATE" && values.includeAssociateL1Progress) {
    input.includeAssociateL1Progress = true;
  }
  if (programType && values.includeOpenDues) {
    input.includeOpenDues = true;
  }
  if ((programType === "ASSOCIATE" || programType === "MEMBER") && values.ineligiblyEmployed) {
    input.ineligiblyEmployed = true;
  }
  if (programType && values.pendingAffiliateRegistration) {
    input.pendingAffiliateRegistration = true;
  }
  if (programType && values.patStatus === "PASSED" && values.expiredPat) {
    input.expiredPat = true;
  }

  return input;
}

export default function CreateTestPersonModal({ onClosed }: { onClosed(): void }) {
  const { Modal, close } = useModal();
  const navigate = useNavigate();
  const [bannerError, setBannerError] = useState("");

  const { data: optionsData, loading: optionsLoading } = useQuery(CREATE_TEST_PERSON_OPTIONS_QUERY);
  const [mutate, { loading: isSubmitting }] = useMutation(CREATE_TEST_PERSON_MUTATION);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { isValid }
  } = useForm<CreateTestPersonFormValues>({
    defaultValues,
    mode: "onChange"
  });

  const selectedProgramType = watch("programType");
  const selectedClassificationId = watch("classificationId");
  const selectedStatus = watch("status");
  const selectedSectionId = watch("sectionId");
  const selectedCompletedLevels = watch("completedPgmLevelCodes");
  const includePreQualifierBand = watch("includePreQualifierBand");
  const hasProgram = Boolean(selectedProgramType);
  const isStudent = selectedProgramType === "STUDENT";
  const isProspect = selectedProgramType === "PROSPECT";
  const isAffiliate = selectedProgramType === "AFFILIATE";
  const isAssociateOrMember = selectedProgramType === "ASSOCIATE" || selectedProgramType === "MEMBER";
  const selectedPatStatus = watch("patStatus");

  useEffect(() => {
    if (selectedPatStatus !== "PASSED") {
      setValue("expiredPat", false);
    }
  }, [selectedPatStatus, setValue]);
  const isProduction = useMemo(() => !isDev(), []);
  const hasProspectBandSelection =
    !isProspect || includePreQualifierBand || prospectQualifierEligibleFromPgmLevels(selectedCompletedLevels);
  const canSave = !isProduction && (!hasProgram || (isValid && hasProspectBandSelection));
  const pgmLevelOptions = useMemo(
    () => (selectedProgramType ? pgmLevelOptionsForProgram(selectedProgramType) : []),
    [selectedProgramType]
  );

  useEffect(() => {
    if (!selectedProgramType || !optionsData) {
      return;
    }

    if (selectedClassificationId && selectedStatus && selectedSectionId) {
      return;
    }

    const defaults = membershipDefaultsForProgram(selectedProgramType, optionsData);
    let updated = false;
    if (!selectedClassificationId && defaults.classificationId) {
      setValue("classificationId", defaults.classificationId, { shouldValidate: true });
      updated = true;
    }
    if (!selectedStatus) {
      setValue("status", defaults.status, { shouldValidate: true });
      updated = true;
    }
    if (!selectedSectionId && defaults.sectionId) {
      setValue("sectionId", defaults.sectionId, { shouldValidate: true });
      updated = true;
    }
    if (updated) {
      trigger(["programType", "classificationId", "status", "sectionId"]);
    }
  }, [
    optionsData,
    selectedProgramType,
    selectedClassificationId,
    selectedStatus,
    selectedSectionId,
    setValue,
    trigger
  ]);

  const sectionOptions = useMemo(
    () => [NONE_OPTION, ...(optionsData?.crmSections.nodes.map(({ id, name }) => ({ value: id, label: name })) ?? [])],
    [optionsData]
  );

  const classificationOptions = useMemo(() => {
    const nodes = optionsData?.classifications.nodes ?? [];
    const filtered = selectedProgramType
      ? nodes.filter(
          (node) => PROGRAM_TYPE_NAME_TO_ENUM[node.programType.name as ProgramTypeName] === selectedProgramType
        )
      : [];

    return [NONE_OPTION, ...filtered.map(({ id, name }) => ({ value: id, label: name }))];
  }, [optionsData, selectedProgramType]);

  const handleProgramSelect = (programType: string) => {
    resetQaOverlayFlags((name, value) => setValue(name, value));

    if (!programType) {
      setValue("classificationId", "");
      setValue("status", "");
      setValue("sectionId", "");
      setValue("completedPgmLevelCodes", []);
      setValue("droppedPgmStudent", false);
      setValue("graduatedPgmStudent", false);
      setValue("includePreQualifierBand", true);
      setValue("patStatus", "NONE");
      return;
    }

    const defaults = membershipDefaultsForProgram(programType, optionsData);
    setValue("classificationId", defaults.classificationId, { shouldValidate: true });
    setValue("status", defaults.status, { shouldValidate: true });
    setValue("sectionId", defaults.sectionId, { shouldValidate: true });
    setValue("completedPgmLevelCodes", defaults.completedPgmLevelCodes);
    setValue("droppedPgmStudent", false);
    setValue("graduatedPgmStudent", false);
    setValue("includePreQualifierBand", true);
    setValue("patStatus", defaultPatStatusForProgram(programType, defaults.status));
    trigger(["programType", "classificationId", "status", "sectionId"]);
  };

  const submit = handleSubmit(async (values) => {
    if (isProduction) {
      return;
    }

    if (
      values.programType === "PROSPECT" &&
      !values.includePreQualifierBand &&
      !prospectQualifierEligibleFromPgmLevels(values.completedPgmLevelCodes)
    ) {
      setBannerError("Error: Select PQ band and/or complete F0 for a QE band.");
      return;
    }

    setBannerError("");

    try {
      const response = await mutate({ variables: { input: toMutationInput(values) } });
      const { person, message = "", success, code } = response.data?.createTestPerson || {};

      if (success && person?.pgaId) {
        showSnackbar(message || "Test person created");
        close();
        navigate("/people/:pgaId/person/contact", { params: { pgaId: person.pgaId } });
        return;
      }

      throw new Error(message || `Request failed with code ${code ?? "unknown"}`);
    } catch (error) {
      setBannerError(`Error: ${(error as ApolloError).message}`);
    }
  });

  return (
    <Modal
      headline="Create Test Person"
      data-testid="create-test-person-modal"
      onClosed={onClosed}
      className="h-fit max-h-[90vh] w-[560px] max-w-[90vw]"
      action={
        <Button
          disabled={!canSave || isSubmitting || optionsLoading}
          onClick={submit}
          iconClassName={classNames({ "[&>md-icon]:animate-spin": isSubmitting })}
          icon={isSubmitting ? "progress_activity" : "check"}
        >
          {isSubmitting ? "Provisioning" : "Save"}
        </Button>
      }
    >
      {!!bannerError && (
        <div className="mb-6">
          <Banner inline variant="error" description="The test person could not be created." subText={bannerError} />
        </div>
      )}

      <div className="mb-6">
        <Banner
          inline
          variant={isProduction ? "error" : "info"}
          description={isProduction ? "Production Environment" : "Development / Sandbox Only"}
          subText={
            isProduction
              ? "This tool is intended for non-production environments only."
              : "Auto-generates identity and program-appropriate membership history. Choose None for a bare CRM person."
          }
        />
      </div>

      <div className="flex flex-col gap-6">
        <ProgramSection
          control={control}
          options={PROGRAM_TYPE_OPTIONS}
          noneOption={NONE_OPTION}
          disabled={optionsLoading}
          onProgramSelect={handleProgramSelect}
        />

        {hasProgram && (
          <MembershipSection
            control={control}
            classificationOptions={classificationOptions}
            statusOptions={STATUS_OPTIONS}
            sectionOptions={sectionOptions}
            noneOption={NONE_OPTION}
            disabled={optionsLoading}
            onStatusSelect={(status) => {
              if (getValues("graduatedPgmStudent")) {
                return;
              }

              const nextPatStatus = defaultPatStatusForProgram(selectedProgramType, status);
              setValue("patStatus", nextPatStatus);
              if (nextPatStatus !== "PASSED") {
                setValue("expiredPat", false);
              }
            }}
          />
        )}

        {hasProgram && pgmLevelOptions.length > 0 && (
          <PgmPathSection
            control={control}
            options={pgmLevelOptions}
            selectedCodes={selectedCompletedLevels}
            programType={selectedProgramType}
            disabled={optionsLoading}
            isProspect={isProspect}
            onToggleLevel={(code, current) => toggleCompletedPgmLevel(selectedProgramType, current, code)}
          />
        )}

        {hasProgram && (
          <EligibilitySection
            control={control}
            isStudent={isStudent}
            isProspect={isProspect}
            isAffiliate={isAffiliate}
            isAssociateOrMember={isAssociateOrMember}
            onStudentPgmOverlayChange={(overlay) => {
              const wasGraduated = getValues("graduatedPgmStudent");
              setValue("droppedPgmStudent", overlay === "dropped");
              setValue("graduatedPgmStudent", overlay === "graduated");

              if (overlay === "graduated") {
                setValue("completedPgmLevelCodes", graduatedStudentCompletedPgmLevels());
                setValue("patStatus", "PASSED");
                return;
              }

              if (!wasGraduated) {
                return;
              }

              setValue("completedPgmLevelCodes", defaultCompletedPgmLevels("STUDENT"));
              setValue("patStatus", defaultPatStatusForProgram("STUDENT", selectedStatus));
            }}
          />
        )}

        {hasProgram && <PatSection control={control} showExpiredPat={selectedPatStatus === "PASSED"} />}
      </div>
    </Modal>
  );
}
